import {
  Card,
  CardBody,
  Col,
  Row,
  InputGroup,
  Input,
  Label,
  Form,
  FormGroup,
  InputGroupText,
  ModalBody,
} from "reactstrap";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { H5, Image, P, Btn } from "../../../../../AbstractElements";
import CommonCardHeader from "../../../../CommonComponents/CommonCardHeader/CommonCardHeader";
import { Link } from "react-router-dom";
import { Save } from "react-feather";
import Swal from "sweetalert2";

// Modals
import UpdateEmpInfoModal from "../../../../UiKits/Modal/Employee/UpdateEmploymentInfo";

// Services
import {
  VIEW_EMPLOYEE_QUERY_KEY,
  employeeService,
} from "../../../../../Services/employeeService";

import {
  VIEW_EMPTYPE_QUERY_KEY,
  employmentTypeService,
} from "../../../../../Services/employmentTypesServices";

// Type
import {
  ViewEmploymentDetails,
  UpdateEmploymentDetailsPayload,
} from "../../../../../Types/Employee/EmployeeModel";

interface EmployeeProfileProps {
  EmpNo: string;
  toggle?: () => void;
}

const UpdateEmpInfo: React.FC<EmployeeProfileProps> = ({ toggle, EmpNo }) => {
  const formatDate = (date?: string) => {
    if (!date) return "";

    const [year, month, day] = date.split("/");

    return `${month}/${day}/${year}`;
  };
  const queryClient = useQueryClient();

  const INITIAL_STATE: ViewEmploymentDetails = {
    application_no: "",
    emp_no: EmpNo,
    full_name: "",
    job_title: "",
    employment_status_label: "",
    employment_type: "",
    hired_date: "",
    start_date: "",
    end_date: "",
    effectivity_date: "",
    length_of_service: "",
  };

  const [formData, setFormData] =
    useState<ViewEmploymentDetails>(INITIAL_STATE);

  const { data: responseData, isLoading: isFetching } = useQuery({
    queryKey: [VIEW_EMPLOYEE_QUERY_KEY, EmpNo],
    queryFn: () => employeeService.viewEmploymentByEmpno(EmpNo),
    enabled: !!EmpNo,
  });

  useEffect(() => {
    if (responseData) {
      setFormData(responseData);
    }
  }, [responseData]);

  const payload = {
    emp_no: EmpNo,
    job_title_id: "",
    job_level: "",
    employment_type: formData.employment_type ?? "",
    employment_status: "",
    effectivity_date: formData.effectivity_date ?? "",
    hired_date: formData.hired_date ?? "",
    start_date: formData.start_date ?? "",
    end_date: formData.end_date ?? "",
  };

  interface ViewEmploymentType {
    id: number;
    name: string;
    status: number;
  }

  // Get Employment Types
  const { data: empTypeResponse, isLoading: empTypesIsLoading } = useQuery<{
    data: ViewEmploymentType[];
  }>({
    queryKey: VIEW_EMPTYPE_QUERY_KEY,
    queryFn: employmentTypeService.viewAllEmploymentType,
  });

  const empTypeData: ViewEmploymentType[] =
    empTypeResponse?.data?.filter((item) => item.status === 1) ?? [];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    const formattedValue =
      e.target.type === "date"
        ? value.replace(/-/g, "/")
        : name === "employment_type"
          ? Number(value)
          : value;

    setFormData({
      ...formData,
      [name]: formattedValue,
    });
  };

  const updateMutation = useMutation({
    mutationFn: (data: typeof payload) =>
      employeeService.updateEmpDetails(EmpNo, data),
    onMutate: () => {
      Swal.fire({
        title: "Processing Request",
        text: "The system is prcoessing the transaction. Please wait...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries();
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "success",
        title: "Transaction Successful",
        text: "Employee record has been updated successfully.",
        confirmButtonColor: "#4e73df",
      }).then((result) => {
        if (result.isConfirmed) {
          Swal.fire({
            title: "Refreshing Employee Information",
            text: "The system is refreshing the updated employee information. Please wait...",
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
              Swal.showLoading();

              setTimeout(() => {
                Swal.close();
              }, 2000);
            },
          });
        }
      });
      if (toggle) toggle();
    },
    onError: (error: any) => {
      const responseData = error.response?.data;
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "error",
        title: "Error",
        text: responseData?.message || "Failed to update application form.",
        confirmButtonColor: "#4e73df",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to update this record?",
      icon: "question",
      showCancelButton: true,

      confirmButtonText: '<i class="fa fa-save"></i> Update Information',
      cancelButtonText: '<i class="fa fa-times"></i> Cancel',

      confirmButtonColor: "#4e73df", // Primary
      cancelButtonColor: "#dc3545", // Danger
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        const latestEmploymentType = String(
          typeof formData.employment_type === "number"
            ? formData.employment_type
            : (empTypeData.find(
                (item) => item.name === formData.employment_type,
              )?.id ?? ""),
        );

        Swal.fire({
          title: "Uploading...",
          text: "Please wait while your profile photo is being uploaded.",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const finalPayload = {
          ...payload,
          employment_type: latestEmploymentType,
        };

        updateMutation.mutate(finalPayload);
      }
    });
  };

  return (
    <ModalBody className="large-modal-body modal-body">
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <div className="d-flex justify-content-between align-items-center">
          <H5> </H5>
          <P className="mb-2">
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>{" "}
            Fields marked with <b className="text-danger">*</b> are required{" "}
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>
          </P>
        </div>
        <Row className="g-3">
          <div className="p-3 card-wrapper border rounded-3 h-100">
            <Row className="p-1">
              <Col sm="6">
                <FormGroup>
                  <Label>Employee No.</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-social-slack txt-primary"></i>
                    </InputGroupText>
                    <Input
                      className="text-color-light"
                      disabled
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData?.emp_no || "* No Record Found *"
                      }
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col sm="6">
                <FormGroup>
                  <Label>Employee Status</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-tag txt-primary"></i>
                    </InputGroupText>
                    <Input
                      className="text-color-light"
                      disabled
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData?.employment_status_label ||
                            "* No Record Found *"
                      }
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>
                    Employment Type <b className="text-danger">*</b>
                  </Label>

                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-tag txt-primary"></i>
                    </InputGroupText>

                    <Input
                      type="select"
                      id="employment_type"
                      name="employment_type"
                      disabled={isFetching || empTypesIsLoading}
                      value={
                        typeof formData.employment_type === "number"
                          ? formData.employment_type
                          : (empTypeData.find(
                              (item) => item.name === formData.employment_type,
                            )?.id ?? "")
                      }
                      required
                      onChange={handleInputChange}
                    >
                      <option value="" disabled>
                        {empTypesIsLoading
                          ? "Loading..."
                          : "Select Employment Type"}
                      </option>

                      {!empTypesIsLoading &&
                        empTypeData.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col sm="6">
                <FormGroup>
                  <Label>
                    Effectivity Date <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-ui-calendar txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="date"
                      id="effectivity_date"
                      name="effectivity_date"
                      onChange={handleInputChange}
                      min={formData.hired_date?.replace(/\//g, "-") || ""}
                      max={formData.end_date?.replace(/\//g, "-") || ""}
                      required
                      value={
                        isFetching
                          ? ""
                          : formData?.effectivity_date?.replace(/\//g, "-") ||
                            ""
                      }
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col sm="6">
                <FormGroup>
                  <Label>
                    Hired Date <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-ui-calendar txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="date"
                      id="hire_date"
                      name="hired_date"
                      max={formData.end_date?.replace(/\//g, "-") || ""}
                      onChange={handleInputChange}
                      required
                      value={
                        isFetching
                          ? ""
                          : formData?.hired_date?.replace(/\//g, "-") || ""
                      }
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col sm="6">
                <FormGroup>
                  <Label>
                    Start Date <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-ui-calendar txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="date"
                      id="start_date"
                      name="start_date"
                      onChange={handleInputChange}
                      min={formData.hired_date?.replace(/\//g, "-") || ""}
                      max={formData.end_date?.replace(/\//g, "-") || ""}
                      required
                      value={
                        isFetching
                          ? ""
                          : formData?.start_date?.replace(/\//g, "-") || ""
                      }
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col sm="6">
                <FormGroup>
                  <Label>End Date</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-ui-calendar txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="date"
                      id="end_date"
                      name="end_date"
                      required={!!formData?.end_date}
                      disabled={!formData?.end_date}
                      onChange={handleInputChange}
                      value={
                        isFetching
                          ? ""
                          : formData?.end_date?.replace(/\//g, "-") || ""
                      }
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col sm="6">
                <FormGroup>
                  <Label>Years of Service</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-briefcase-alt-1 txt-primary"></i>
                    </InputGroupText>
                    <Input
                      className="text-color-light"
                      disabled
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData?.length_of_service || "* No Record Found *"
                      }
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <div className="d-flex justify-content-end">
                <Col md="4">
                  <Btn
                    color="primary"
                    className="w-100"
                    type="submit"
                    disabled={updateMutation.isPending}
                  >
                    <Save size={16} className="me-1" />
                    {updateMutation.isPending ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Updating...
                      </>
                    ) : (
                      "Update Record"
                    )}
                  </Btn>
                </Col>
              </div>
            </Row>
          </div>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default UpdateEmpInfo;
